import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Tag } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useCatalog, isHiddenById } from "@/context/CatalogContext";
import { resolveAssetUrl, optimizeStoreImage } from "@/lib/assetUrl";
import {
  ALL_PRODUCTS,
  SHEIN_SUBCATEGORIES,
  computeDiscountedPrice,
  getProductDiscountPct,
  formatPrice,
  localized,
  productSlug,
} from "@/data/products";

const ROTATE_MS = 4000;

// Pick one representative product per subcategory (in a fixed display order)
// so the strip always shows visual variety: a women's item, then shoes, bag, etc.
const DISPLAY_ORDER = ["women", "shoes", "accessories", "beauty", "kids"];

const pickFeatured = () => {
  const all = ALL_PRODUCTS.filter(
    (p) => p.category === "shein" && p.originalPrice != null && p.images?.length && !isHiddenById(p.id)
  );
  const seenIds = new Set();
  const picks = [];
  for (const sc of DISPLAY_ORDER) {
    const candidate = all.find(
      (p) => p.sheinCategory === sc && !seenIds.has(p.id)
    );
    if (candidate) {
      seenIds.add(candidate.id);
      picks.push(candidate);
    }
  }
  // Fill up to 5 with any leftover SHEIN product
  for (const p of all) {
    if (picks.length >= 5) break;
    if (seenIds.has(p.id)) continue;
    seenIds.add(p.id);
    picks.push(p);
  }
  return picks.slice(0, 5);
};

export default function SheinFeaturedLooks() {
  const { t, lang, isRTL } = useLanguage();
  const SH = t.shein;
  const navigate = useNavigate();
  const { version: catalogVersion } = useCatalog();
  const looks = useMemo(() => pickFeatured(), [catalogVersion]);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (paused || looks.length <= 1) return;
    const id = window.setInterval(() => {
      setIdx((i) => (i + 1) % looks.length);
    }, ROTATE_MS);
    return () => window.clearInterval(id);
  }, [paused, looks.length]);

  useEffect(() => {
    if (!looks.length) return;
    setImageError(false);
  }, [looks.length, looks[idx]?.id, looks[idx]?.images?.[0]]);

  if (looks.length === 0) return null;

  const current = looks[idx];
  const coverImage = resolveAssetUrl(current.images[0]);
  const discountPct = getProductDiscountPct(current);
  const discounted = computeDiscountedPrice(current.originalPrice, discountPct);
  const name = localized(current.name, lang);
  const subcatLabel = (() => {
    const sc = SHEIN_SUBCATEGORIES.find((s) => s.id === current.sheinCategory);
    return sc ? localized(sc.label, lang) : "";
  })();

  const go = (i) => setIdx((i + looks.length) % looks.length);

  return (
    <section
      className="relative max-w-7xl mx-auto mt-8 md:mt-10 px-1"
      data-testid="shein-featured-looks"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="mb-4 flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#111] text-white text-[10px] font-bold tracking-[0.25em] uppercase">
          <Sparkles className="w-3 h-3" strokeWidth={2.5} />
          {SH.featuredEyebrow}
        </span>
        <span className="text-xs font-medium text-[#666] sm:text-sm dark:text-[#BAC4D4]">
          {SH.featuredHint}
        </span>
      </div>

      <button
        type="button"
        onClick={() => navigate(`/stock/${productSlug(current.id)}`)}
        className="group relative block w-full aspect-[16/9] sm:aspect-[2/1] md:aspect-[5/2] rounded-3xl overflow-hidden bg-[#FFF1F6] ring-1 ring-black/5 focus:outline-none focus:ring-2 focus:ring-[#F37021]"
        data-testid="shein-featured-look-active"
        aria-label={`${name} — ${formatPrice(discounted)}`}
      >
        <AnimatePresence mode="wait">
          {!coverImage || imageError ? (
            <motion.div
              key={`${current.id}-fallback`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="absolute inset-0 flex h-full w-full flex-col items-center justify-center bg-[radial-gradient(circle_at_top,rgba(243,112,33,0.16),transparent_38%),linear-gradient(145deg,#2b0f1e_0%,#4b1736_48%,#6a2147_100%)] px-6 text-center text-white"
            >
              <div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.35em] text-white/60">
                {SH.featuredEyebrow}
              </div>
              <div className="max-w-md font-display text-2xl font-bold leading-tight sm:text-3xl">
                {name}
              </div>
            </motion.div>
          ) : (
            <motion.img
              key={current.id}
              src={optimizeStoreImage(coverImage, { width: 1100, quality: 72 })}
              alt={name}
              loading="lazy"
              onError={(e) => {
                if (e.currentTarget.src !== coverImage) {
                  e.currentTarget.src = coverImage;
                  return;
                }
                setImageError(true);
              }}
              initial={{ opacity: 0, scale: 1.06 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.02 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}
        </AnimatePresence>

        {/* Vignette / readable overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/15 to-transparent pointer-events-none" />

        {/* 30% OFF chip */}
        <span
          className="absolute top-4 end-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur text-[#111] text-[11px] font-extrabold tracking-wider"
          data-testid="shein-featured-discount"
        >
          <Tag className="w-3 h-3" strokeWidth={2.5} />
          -{discountPct}%
        </span>

        {/* Bottom caption */}
        <div className="absolute bottom-0 inset-x-0 p-5 sm:p-6 md:p-8 text-white">
          <div className="flex items-end justify-between gap-4">
            <div className="min-w-0">
              <div className="text-[10px] sm:text-xs font-semibold tracking-[0.3em] uppercase opacity-80 mb-1.5">
                {subcatLabel}
              </div>
              <div
                className="font-display text-lg sm:text-2xl md:text-3xl font-bold leading-tight truncate"
                data-testid="shein-featured-name"
              >
                {name}
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-base sm:text-xl font-bold">
                  {formatPrice(discounted)}
                </span>
                <span className="text-xs sm:text-sm opacity-70 line-through">
                  {formatPrice(current.originalPrice)}
                </span>
              </div>
            </div>
            <span className="hidden sm:inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-[#F37021] text-white text-sm font-semibold opacity-90 group-hover:opacity-100 group-hover:-translate-y-0.5 transition-all">
              {SH.featuredShopCta}
            </span>
          </div>
        </div>
      </button>

      {/* Progress dots */}
      {looks.length > 1 && (
        <div
          className="mt-4 flex items-center justify-center gap-2"
          data-testid="shein-featured-dots"
        >
          {looks.map((p, i) => {
            const active = i === idx;
            return (
              <button
                key={p.id}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  go(i);
                }}
                aria-label={`${SH.featuredGoTo} ${i + 1}`}
                aria-current={active ? "true" : "false"}
                className={`h-1.5 rounded-full transition-all ${
                  active ? "w-8 bg-[#111] dark:bg-white" : "w-2 bg-black/20 hover:bg-black/40 dark:bg-white/20 dark:hover:bg-white/40"
                }`}
                data-testid={`shein-featured-dot-${i}`}
              />
            );
          })}
        </div>
      )}
    </section>
  );
}
